// Nosh PWA Notification Service
// Handles push notifications for expiring items, user preferences, and device management

import { supabase } from './supabase';
import { daysUntilExpiry } from './utils';

// ==================== TYPES ====================

export interface NotificationPreferences {
  id: string;
  user_id: string;
  expiry_notifications_enabled: boolean;
  daily_summary_enabled: boolean;
  notification_time: string; // Time in HH:MM:SS format
  timezone: string;
  notify_days_before: number[]; // Array of days before expiry
  max_notifications_per_day: number;
  quiet_hours_enabled: boolean;
  quiet_start_time: string;
  quiet_end_time: string;
  sound_enabled: boolean;
  vibration_enabled: boolean;
  created_at: string;
  updated_at: string;
}

export interface StoredPushSubscription {
  id: string;
  user_id: string;
  endpoint: string;
  p256dh_key: string;
  auth_key: string;
  user_agent?: string;
  device_name?: string;
  is_active: boolean;
  last_used: string;
  created_at: string;
}

export interface NotificationData {
  type: 'expiry_today' | 'expiry_tomorrow' | 'daily_summary';
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  data?: any;
  actions?: NotificationAction[];
}

interface NotificationAction {
  action: string;
  title: string;
  icon?: string;
}

interface FoodItem {
  id: string;
  name: string;
  emoji: string;
  expiryDate: string;
  location: 'fridge' | 'pantry' | 'freezer';
  quantity: number;
  unit: string;
}

// ==================== NOTIFICATION SERVICE CLASS ====================

export class NotificationService {
  private static instance: NotificationService;
  private swRegistration: ServiceWorkerRegistration | null = null;
  private subscription: PushSubscription | null = null;

  // VAPID public key from environment variables
  // Generate VAPID keys using: npx web-push generate-vapid-keys
  private readonly VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY || '';

  constructor() {
    this.init();
  }

  static getInstance(): NotificationService {
    if (!NotificationService.instance) {
      NotificationService.instance = new NotificationService();
    }
    return NotificationService.instance;
  }

  // ==================== INITIALIZATION ====================

  private async init(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        this.swRegistration = await navigator.serviceWorker.ready;
        console.log('🔔 Notification service initialized');
        
        // Update user timezone automatically
        await this.updateUserTimezone();
      } catch (error) {
        console.error('❌ Failed to initialize notification service:', error);
      }
    }
  }

  // ==================== PERMISSION MANAGEMENT ====================

  async requestPermission(): Promise<NotificationPermission> {
    if (!('Notification' in window)) {
      console.warn('⚠️ This browser does not support notifications');
      return 'denied';
    }

    // Check current permission
    if (Notification.permission === 'granted') {
      return 'granted';
    }

    if (Notification.permission === 'denied') {
      return 'denied';
    }

    // Request permission
    try {
      const permission = await Notification.requestPermission();
      console.log(`🔔 Notification permission: ${permission}`);
      return permission;
    } catch (error) {
      console.error('❌ Error requesting notification permission:', error);
      return 'denied';
    }
  }

  getPermissionStatus(): NotificationPermission {
    if (!('Notification' in window)) {
      return 'denied';
    }
    return Notification.permission;
  }

  // ==================== PUSH SUBSCRIPTION MANAGEMENT ====================

  async subscribeToPush(): Promise<PushSubscription | null> {
    if (!this.swRegistration) {
      console.error('❌ Service Worker not available');
      return null;
    }

    if (this.getPermissionStatus() !== 'granted') {
      console.error('❌ Notification permission not granted');
      return null;
    }

    try {
      // Check for existing subscription
      let subscription = await this.swRegistration.pushManager.getSubscription();

      if (!subscription) {
        // Create new subscription
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.VAPID_PUBLIC_KEY)
        });
      }

      // Save subscription to database
      await this.savePushSubscription(subscription);
      this.subscription = subscription;
      
      console.log('✅ Push subscription created/updated');
      return subscription;
    } catch (error) {
      console.error('❌ Failed to subscribe to push notifications:', error);
      return null;
    }
  }

  async unsubscribeFromPush(): Promise<boolean> {
    if (!this.swRegistration) {
      return false;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await this.removePushSubscription(subscription.endpoint);
        this.subscription = null;
        console.log('✅ Push subscription removed');
      }
      return true;
    } catch (error) {
      console.error('❌ Failed to unsubscribe from push notifications:', error);
      return false;
    }
  }

  private async savePushSubscription(subscription: PushSubscription): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      throw new Error('User not authenticated');
    }

    const subscriptionData = subscription.toJSON() as any;
    const keys = subscriptionData.keys;

    if (!keys?.p256dh || !keys?.auth) {
      throw new Error('Invalid subscription keys');
    }

    const deviceName = this.getDeviceName();

    const { error } = await supabase
      .from('push_subscriptions')
      .upsert({
        user_id: userId,
        endpoint: subscription.endpoint,
        p256dh_key: keys.p256dh,
        auth_key: keys.auth,
        user_agent: navigator.userAgent,
        device_name: deviceName,
        is_active: true,
        last_used: new Date().toISOString()
      }, {
        onConflict: 'endpoint'
      });

    if (error) {
      throw new Error(`Failed to save push subscription: ${error.message}`);
    }
  }

  private async removePushSubscription(endpoint: string): Promise<void> {
    const { error } = await supabase
      .from('push_subscriptions')
      .update({ is_active: false })
      .eq('endpoint', endpoint);

    if (error) {
      console.error('❌ Failed to remove push subscription from database:', error);
    }
  }

  // ==================== NOTIFICATION PREFERENCES ====================

  async getNotificationPreferences(): Promise<NotificationPreferences | null> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return null;
    }

    const { data, error } = await supabase
      .from('notification_preferences')
      .select('*')
      .eq('user_id', userId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No preferences found, create default
        return await this.createDefaultPreferences(userId);
      }
      console.error('❌ Failed to fetch notification preferences:', error);
      return null;
    }

    return data;
  }

  async updateNotificationPreferences(preferences: Partial<NotificationPreferences>): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return false;
    }

    const { error } = await supabase
      .from('notification_preferences')
      .upsert({
        user_id: userId,
        ...preferences,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'user_id'
      });

    if (error) {
      console.error('❌ Failed to update notification preferences:', error);
      return false;
    }

    console.log('✅ Notification preferences updated');
    return true;
  }

  private async createDefaultPreferences(userId: string): Promise<NotificationPreferences> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    
    const defaultPrefs = {
      user_id: userId,
      expiry_notifications_enabled: true,
      daily_summary_enabled: false,
      notification_time: '17:00:00', // 5 PM
      timezone: timezone,
      notify_days_before: [0, 1], // Today and tomorrow
      max_notifications_per_day: 1,
      quiet_hours_enabled: false,
      quiet_start_time: '22:00:00', // 10 PM
      quiet_end_time: '08:00:00', // 8 AM
      sound_enabled: true,
      vibration_enabled: true
    };

    const { data, error } = await supabase
      .from('notification_preferences')
      .insert(defaultPrefs)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create default preferences: ${error.message}`);
    }

    return data;
  }

  // ==================== NOTIFICATION DISPLAY ====================

  async showLocalNotification(notificationData: NotificationData): Promise<void> {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      console.warn('⚠️ Cannot show notification: permission not granted');
      return;
    }

    // Check if we're in quiet hours
    const preferences = await this.getNotificationPreferences();
    if (preferences && await this.isInQuietHours(preferences)) {
      console.log('🔕 Notification suppressed: quiet hours');
      return;
    }

    try {
      const notificationOptions: NotificationOptions = {
        body: notificationData.body,
        icon: notificationData.icon || '/nosh-new-icon.png',
        badge: notificationData.badge || '/nosh-new-icon.png',
        data: notificationData.data,
        requireInteraction: true,
        silent: preferences ? !preferences.sound_enabled : false
      };

      // Add vibration if supported
      if (preferences?.vibration_enabled && 'vibrate' in navigator) {
        navigator.vibrate([200, 100, 200]);
      }

      const notification = new Notification(notificationData.title, notificationOptions);

      // Auto-close after 10 seconds
      setTimeout(() => {
        notification.close();
      }, 10000);

      // Handle notification click
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Navigate to inventory if it's an expiry notification
        if (notificationData.type.startsWith('expiry_')) {
          window.location.href = '/inventory';
        }
      };

    } catch (error) {
      console.error('❌ Failed to show local notification:', error);
    }
  }

  // ==================== EXPIRY NOTIFICATIONS ====================

  async checkAndNotifyExpiringItems(): Promise<void> {
    console.log('🔍 Checking for expiring items...');
    
    const preferences = await this.getNotificationPreferences();
    if (!preferences || !preferences.expiry_notifications_enabled) {
      console.log('📵 Expiry notifications disabled');
      return;
    }

    // Check if we've already sent notifications today
    const hasNotifiedToday = await this.hasNotificationBeenSent('expiry_today');
    const hasNotifiedTomorrow = await this.hasNotificationBeenSent('expiry_tomorrow');

    if (hasNotifiedToday && hasNotifiedTomorrow) {
      console.log('✅ All expiry notifications already sent today');
      return;
    }

    const expiringItems = await this.getExpiringItems();
    if (expiringItems.length === 0) {
      console.log('✅ No expiring items found');
      return;
    }

    const groupedItems = this.groupItemsByExpiryDays(expiringItems);

    // Send notifications for each day
    for (const [days, items] of groupedItems) {
      if (preferences.notify_days_before.includes(days)) {
        const notificationType = days === 0 ? 'expiry_today' : 'expiry_tomorrow';
        
        // Check if we've already sent this specific notification type
        const alreadySent = await this.hasNotificationBeenSent(notificationType);
        if (!alreadySent) {
          await this.sendExpiryNotification(days, items);
        }
      }
    }
  }

  private async getExpiringItems(): Promise<FoodItem[]> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return [];
    }

    // Get items expiring in the next 2 days
    const twoDaysFromNow = new Date();
    twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);

    const { data, error } = await supabase
      .from('food_items')
      .select('id, name, emoji, expiry_date, location, quantity, unit')
      .eq('user_id', userId)
      .lte('expiry_date', twoDaysFromNow.toISOString().split('T')[0])
      .order('expiry_date', { ascending: true });

    if (error) {
      console.error('❌ Failed to fetch expiring items:', error);
      return [];
    }

    // Transform to FoodItem format and filter frozen items
    return data
      .filter(item => item.location !== 'freezer') // Frozen items don't expire the same way
      .map(item => ({
        id: item.id,
        name: item.name,
        emoji: item.emoji,
        expiryDate: item.expiry_date,
        location: item.location,
        quantity: item.quantity,
        unit: item.unit
      }));
  }

  private groupItemsByExpiryDays(items: FoodItem[]): Map<number, FoodItem[]> {
    const grouped = new Map<number, FoodItem[]>();

    items.forEach(item => {
      const days = daysUntilExpiry(item.expiryDate);
      if (!grouped.has(days)) {
        grouped.set(days, []);
      }
      grouped.get(days)!.push(item);
    });

    return grouped;
  }

  private async sendExpiryNotification(days: number, items: FoodItem[]): Promise<void> {
    const count = items.length;
    let title: string;
    let body: string;
    let type: 'expiry_today' | 'expiry_tomorrow';

    if (days === 0) {
      type = 'expiry_today';
      title = count === 1 ? '🚨 Item Expiring Today!' : `🚨 ${count} Items Expiring Today!`;
      body = count === 1 
        ? `${items[0].emoji} ${items[0].name} expires today`
        : `${items.map(item => `${item.emoji} ${item.name}`).join(', ')} expire today`;
    } else if (days === 1) {
      type = 'expiry_tomorrow';
      title = count === 1 ? '⏰ Item Expiring Tomorrow!' : `⏰ ${count} Items Expiring Tomorrow!`;
      body = count === 1 
        ? `${items[0].emoji} ${items[0].name} expires tomorrow`
        : `${items.map(item => `${item.emoji} ${item.name}`).join(', ')} expire tomorrow`;
    } else {
      // For other days (shouldn't happen with current logic, but safety)
      type = 'expiry_tomorrow';
      title = count === 1 ? `⏰ Item Expiring in ${days} Days!` : `⏰ ${count} Items Expiring in ${days} Days!`;
      body = count === 1 
        ? `${items[0].emoji} ${items[0].name} expires in ${days} days`
        : `${items.map(item => `${item.emoji} ${item.name}`).join(', ')} expire in ${days} days`;
    }

    // Show local notification
    await this.showLocalNotification({
      type,
      title,
      body,
      icon: '/nosh-new-icon.png',
      data: {
        items: items.map(item => item.id),
        type: 'expiry'
      }
    });

    // Record notification in database
    await this.recordNotificationSent(
      type,
      title,
      body,
      items.map(item => item.id)
    );

    console.log(`✅ Sent ${type} notification for ${count} items`);
  }

  // ==================== NOTIFICATION HISTORY ====================

  private async hasNotificationBeenSent(type: string): Promise<boolean> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return false;
    }

    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('notification_history')
      .select('id')
      .eq('user_id', userId)
      .eq('notification_type', type)
      .gte('sent_at', `${today}T00:00:00Z`)
      .lt('sent_at', `${today}T23:59:59Z`)
      .limit(1);

    if (error) {
      console.error('❌ Failed to check notification history:', error);
      return false;
    }

    return data.length > 0;
  }

  private async recordNotificationSent(
    type: string, 
    title: string, 
    body: string, 
    itemIds: string[]
  ): Promise<void> {
    const userId = await this.getCurrentUserId();
    if (!userId) {
      return;
    }

    const { error } = await supabase
      .from('notification_history')
      .insert({
        user_id: userId,
        notification_type: type,
        title,
        body,
        food_item_ids: itemIds,
        total_items: itemIds.length,
        delivery_status: 'sent'
      });

    if (error) {
      console.error('❌ Failed to record notification history:', error);
    }
  }

  // ==================== UTILITY METHODS ====================

  private async getCurrentUserId(): Promise<string | null> {
    const { data: { user } } = await supabase.auth.getUser();
    return user?.id || null;
  }

  private getDeviceName(): string {
    const userAgent = navigator.userAgent;
    
    if (userAgent.includes('iPhone')) return 'iPhone';
    if (userAgent.includes('iPad')) return 'iPad';
    if (userAgent.includes('Android')) return 'Android';
    if (userAgent.includes('Mac')) return 'Mac';
    if (userAgent.includes('Windows')) return 'Windows';
    if (userAgent.includes('Linux')) return 'Linux';
    
    return 'Unknown Device';
  }

  private urlBase64ToUint8Array(base64String: string): Uint8Array {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  async updateUserTimezone(): Promise<void> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    await this.updateNotificationPreferences({ timezone });
  }

  private async isInQuietHours(preferences: NotificationPreferences): Promise<boolean> {
    if (!preferences.quiet_hours_enabled) {
      return false;
    }

    const now = new Date();
    const currentTime = now.toLocaleTimeString('en-US', { 
      hour12: false, 
      timeZone: preferences.timezone 
    });

    const quietStart = preferences.quiet_start_time;
    const quietEnd = preferences.quiet_end_time;

    // Handle quiet hours that span midnight
    if (quietStart > quietEnd) {
      return currentTime >= quietStart || currentTime <= quietEnd;
    } else {
      return currentTime >= quietStart && currentTime <= quietEnd;
    }
  }

  async cleanup(): Promise<void> {
    if (this.subscription) {
      await this.unsubscribeFromPush();
    }
    this.swRegistration = null;
    console.log('🧹 Notification service cleaned up');
  }
}

// ==================== SINGLETON EXPORT ====================

export const notificationService = NotificationService.getInstance(); 